import { useTranslation } from "react-i18next";
import useUserCurrency from "./useUserCurrency";
import type { Cents } from "@/types/pricing.type";
import { getLocaleFromCurrency } from "@/lib/currency";

const useFormat = () => {
  const currency = useUserCurrency();
  const locale = getLocaleFromCurrency(currency);
  // The language picked in the app, not the browser's: a count's decimal
  // separator matches the copy around it.
  const language = useTranslation().i18n.resolvedLanguage;

  /**
   * The single display edge: takes integer cents and converts to major units
   * exactly once, right before `Intl.NumberFormat`. Callers pass {@link Cents}
   * so a major-unit value can't reach here by mistake.
   */
  const formatPrice = (
    amount: Cents,
    options?: Pick<Intl.NumberFormatOptions, "minimumFractionDigits">,
  ) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      ...options,
    }).format(amount / 100);
  };

  const getCurrencySymbol = () =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    })
      .formatToParts(1)
      .find((part) => part.type === "currency")?.value;

  const formatNumber = (number: number) => {
    return Number(number.toFixed(1)).toLocaleString(language);
  };

  /**
   * The compact count of the app: `999`, `1.5K`, `1.2M`, `1.2B` — one
   * decimal, no trailing `.0`. Each unit is rounded before choosing the next,
   * so 999,999 reads `1M`, never `1000K`. Rounded on whole numbers: 1,150 / 100
   * is exactly 11.5, while 1.15 is stored as 1.1499… and `toFixed` would round
   * it down.
   */
  const formatViewsNumber = (number: number) => {
    if (number < 1_000) return formatNumber(number);
    const thousands = Math.round(number / 100) / 10;
    if (thousands < 1_000) return formatNumber(thousands) + "K";
    const millions = Math.round(number / 100_000) / 10;
    if (millions < 1_000) return formatNumber(millions) + "M";
    return formatNumber(Math.round(number / 100_000_000) / 10) + "B";
  };

  const getTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return {
      fullSeconds: seconds,
      hours,
      minutes,
      seconds: remainingSeconds,
    };
  };
  /**
   * Get resized thumbnail url
   * @param {string} thumbnail - Original thumbnail url
   * @param {string} height - Height of the resized thumbnail
   * @param {string} extension - Extension of the resized thumbnail
   * @returns {string} Resized thumbnail url (e.g. https://example.com/thumbnail/1080/thumbnailName.jpg)
   */
  const getResizedThumbnailUrl = (
    thumbnail: string,
    height: string,
    extension?: string,
  ) => {
    const url = thumbnail.split("/");
    url.splice(3, 0, height);

    const newUrl = url.join("/");

    if (extension) return newUrl.replace(/\.\w+$/, `.${extension}`);

    return newUrl;
  };

  return {
    getResizedThumbnailUrl,
    getCurrencySymbol,
    formatNumber,
    formatPrice,
    currency,
    getTime,
    formatViewsNumber,
  };
};

export default useFormat;
